import {
  SearchAnalyticsSession,
  type SearchEndReason,
  type SearchRankingVariant,
  type SearchResultMetadata,
} from './searchAnalytics';

export type PeopleTypeaheadContext =
  | 'delegate_editor'
  | 'team_member_editor'
  | 'workstream_filter'
  | 'workstream_people_scope'
  | 'people_search_menu'
  | 'employee_picker'
  | 'other';

export type TypeaheadResult = {
  localIdentity: string;
  rank: number;
  metadata?: SearchResultMetadata;
};

/** Employee-typeahead adapter over the shared GLOBAL_SEARCH_* session mechanics. */
export class PeopleTypeaheadAnalyticsSession extends SearchAnalyticsSession {
  private committed = false;
  private showMoreUsed = false;

  constructor(
    private readonly context: PeopleTypeaheadContext,
    rankingVariant: SearchRankingVariant
  ) {
    super(rankingVariant);
    this.log('GLOBAL_SEARCH_SESSION_STARTED', {
      search_session_id: this.sessionId,
      surface: 'employee_typeahead',
      typeahead_context: context,
      entry_point: 'not_applicable',
      initial_filter: 'not_applicable',
      ranking_variant: rankingVariant,
    });
  }

  resultsViewed({
    query,
    resultCount,
    shownTopK,
    elapsedMs,
    rankingVariant,
  }: {
    query: string;
    resultCount: number;
    shownTopK: number;
    elapsedMs: number;
    rankingVariant: SearchRankingVariant;
  }) {
    const requestId = this.beginRequest(resultCount, rankingVariant);
    this.log('GLOBAL_SEARCH_RESULTS_VIEWED', {
      ...this.requestProperties(requestId),
      previous_filter: 'not_applicable',
      filter_change_sequence: 0,
      ...this.requestQueryProperties(query, resultCount, shownTopK, elapsedMs),
      ranking_variant: rankingVariant,
    });
    return requestId;
  }

  resultsImpressed(requestId: string, results: TypeaheadResult[]) {
    for (const result of results) {
      this.log('GLOBAL_SEARCH_RESULT_IMPRESSED', {
        ...this.requestProperties(requestId),
        ...this.impressionProperties({...result, entityType: 'person'}, 4),
        ranking_variant: this.rankingVariant,
      });
    }
  }

  resultSelected({
    requestId,
    result,
    selectionMethod,
    rankingVariant,
  }: {
    requestId: string;
    result: TypeaheadResult;
    selectionMethod: 'click' | 'keyboard';
    rankingVariant: SearchRankingVariant;
  }) {
    this.log('GLOBAL_SEARCH_RESULT_SELECTED', {
      ...this.requestProperties(requestId),
      ...this.selectionProperties({...result, entityType: 'person'}),
      selection_method: selectionMethod,
      show_more_before_selection: this.showMoreUsed,
      ranking_variant: rankingVariant,
    });
  }

  selectionCommitted() {
    this.committed = true;
    this.end('selected');
  }

  showMore() {
    this.showMoreUsed = true;
  }

  end(endReason: SearchEndReason = 'dismissed') {
    if (!this.endOnce()) return;
    this.log('GLOBAL_SEARCH_SESSION_ENDED', {
      search_session_id: this.sessionId,
      surface: 'employee_typeahead',
      active_filter: 'not_applicable',
      typeahead_context: this.context,
      ...this.terminalProperties(),
      qualified_selection: this.committed,
      last_active_filter: 'not_applicable',
      filter_changed: false,
      end_reason: endReason,
      outcome: this.committed ? 'selection_committed' : 'not_applicable',
    });
  }

  private requestProperties(requestId: string) {
    return {
      search_session_id: this.sessionId,
      request_id: requestId,
      request_sequence: this.requestSequence,
      surface: 'employee_typeahead',
      active_filter: 'not_applicable',
      typeahead_context: this.context,
    };
  }
}
